import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import EliosSidebar from './EliosSidebar';
import EliosBottomTabBar from './EliosBottomTabBar';
import BackButton from './BackButton';
import PWAInstallBanner from '../PWAInstallBanner';
import { UsageLimitBanner } from '../elios/UsageLimitBanner';
import { EliosNotificationBell } from '../elios/EliosNotificationBell';
import EliosErrorBoundary from '../elios/EliosErrorBoundary';
import WhatsAppBubble from '../elios/WhatsAppBubble';
import OnboardingTour from '../OnboardingTour';
import OnboardingWizard from '../elios/OnboardingWizard';
import { ShieldAlert, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ImpersonationBanner = ({ user }) => {
  // The impersonation session lives in sessionStorage (per-tab) so the
  // operator's original admin tab is untouched.
  const [remainingSecs, setRemainingSecs] = useState(null);
  const [warned, setWarned] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('impersonation_active') !== '1') return;

    // If no expiry is set (old session), set one so the countdown works.
    if (!sessionStorage.getItem('impersonation_expires_at')) {
      sessionStorage.setItem('impersonation_expires_at', String(Date.now() + 15 * 60 * 1000));
    }

    const tick = () => {
      const exp = parseInt(sessionStorage.getItem('impersonation_expires_at') || '0', 10);
      const secs = Math.max(0, Math.floor((exp - Date.now()) / 1000));
      setRemainingSecs(secs);
      if (secs === 60 && !warned) {
        setWarned(true);
        toast.warning('Sessione impersonation: 1 minuto alla scadenza.', { duration: 6000 });
      }
      if (secs <= 0) {
        // Graceful auto-exit: clear session impersonation but keep the admin's
        // own localStorage token intact so they don't get logged out.
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('impersonation_active');
        sessionStorage.removeItem('impersonation_expires_at');
        toast.info('Sessione impersonation scaduta. Torni al pannello admin.', { duration: 4000 });
        setTimeout(() => {
          window.location.href = localStorage.getItem('token') ? '/admin-console' : '/elios/login';
        }, 800);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [warned]);

  if (typeof window === 'undefined') return null;
  if (sessionStorage.getItem('impersonation_active') !== '1') return null;

  const handleExit = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('impersonation_active');
    sessionStorage.removeItem('impersonation_expires_at');
    // Return to admin if the operator still has an admin session, otherwise to the Elios login.
    window.location.href = localStorage.getItem('token') ? '/admin-console' : '/elios/login';
  };

  const fmt = (s) => {
    if (s == null) return '';
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };
  const isLowTime = remainingSecs != null && remainingSecs <= 60;

  return (
    <div
      className={`fixed top-3 left-1/2 -translate-x-1/2 z-[70] text-white px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-2 sm:gap-3 text-xs sm:text-sm shadow-lg rounded-full max-w-[calc(100%-1.5rem)] transition-colors ${
        isLowTime ? 'bg-red-500' : 'bg-amber-500'
      }`}
      data-testid="impersonation-banner"
    >
      <ShieldAlert className="w-4 h-4 shrink-0" />
      <span className="truncate min-w-0">
        <span className="hidden sm:inline">Sei entrato come </span>
        <strong>{user?.name || user?.email}</strong>
        <span className="hidden md:inline"> in modalità <strong>impersonation</strong>. Ogni azione è registrata.</span>
        <span className="inline md:hidden"> · imp.</span>
      </span>
      {remainingSecs != null && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 font-mono text-[11px] sm:text-[12px] shrink-0"
          data-testid="impersonation-countdown"
          title="Tempo rimanente alla scadenza della sessione impersonation"
        >
          {fmt(remainingSecs)}
        </span>
      )}
      <button
        onClick={handleExit}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 hover:bg-white/30 text-[11px] sm:text-[12px] font-medium transition-colors shrink-0"
        data-testid="impersonation-exit-btn"
      >
        <LogOut className="w-3 h-3" /> Esci
      </button>
    </div>
  );
};

// Forces a B2B client to complete the first-access onboarding questionnaire
// before using the app. Admins/superadmins and impersonation sessions are never
// gated; the wizard only appears when the admin-controlled `onboarding_required`
// flag is on and the questionnaire is not yet completed.
const OnboardingGate = ({ user, children }) => {
  const isImpersonation =
    typeof window !== 'undefined' && sessionStorage.getItem('impersonation_active') === '1';
  const gated = user?.role === 'elios_client' && !isImpersonation;

  const [state, setState] = useState({
    loading: gated, show: false, channels: [], answers: {}, companyName: '',
  });

  useEffect(() => {
    if (!gated) { setState(s => ({ ...s, loading: false, show: false })); return; }
    let active = true;
    api.get('/elios-client/onboarding/status')
      .then(r => {
        if (!active) return;
        const d = r.data || {};
        setState({
          loading: false,
          show: !!d.required && !d.completed,
          channels: d.required_channels || [],
          answers: d.answers || {},
          companyName: d.company_name || '',
        });
      })
      .catch(() => { if (active) setState(s => ({ ...s, loading: false, show: false })); });
    return () => { active = false; };
  }, [gated]);

  if (!gated) return children;
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1C]">
        <div className="w-12 h-12 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (state.show) {
    return (
      <OnboardingWizard
        requiredChannels={state.channels}
        companyName={state.companyName}
        initialAnswers={state.answers}
        onDone={() => setState(s => ({ ...s, show: false }))}
      />
    );
  }
  return children;
};

const EliosLayout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1C]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Caricamento piattaforma...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/elios/login" replace />;
  if (!['elios_client', 'superadmin', 'admin', 'vice_superadmin'].includes(user.role)) return <Navigate to="/dashboard" replace />;

  return (
    <OnboardingGate user={user}>
    <div className="flex flex-col min-h-screen bg-[#FAFAFA]">
      <ImpersonationBanner user={user} />
      <UsageLimitBanner />
      <div className="flex flex-1 min-h-0">
        <EliosSidebar />
        <main className="flex-1 min-w-0 overflow-x-hidden">
          {/* Mobile top bar */}
          <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100 px-4 py-2.5 flex items-center justify-end lg:hidden">
            <EliosNotificationBell apiPrefix="/elios-client" />
          </div>
          {/* Desktop notification bell */}
          <div className="hidden lg:flex fixed top-4 right-10 z-20">
            <EliosNotificationBell apiPrefix="/elios-client" />
          </div>
          <div className="p-4 sm:p-6 lg:p-10 pt-2 lg:pt-10 pb-24 lg:pb-10">
            <BackButton />
            {/* R3 — ErrorBoundary impedisce che un crash in una singola
                pagina Elios (es. oggetto passato a toast.error, render di
                undefined, etc.) porti giù l'intera SPA. Ogni navigazione
                resetta lo state grazie alla `key` derivata da pathname. */}
            <EliosErrorBoundary key={location.pathname} pageName="pagina Elios">
              <Outlet />
            </EliosErrorBoundary>
          </div>
        </main>
      </div>
      <EliosBottomTabBar />
      <OnboardingTour />
      <PWAInstallBanner />
      {user.role === 'elios_client' && <WhatsAppBubble />}
    </div>
    </OnboardingGate>
  );
};

export default EliosLayout;
